import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, MapPin, Car, Star } from 'lucide-react';
import { NEIGHBORHOODS, STUDIO, IMAGES, REVIEWS, VIDEOS } from '../data/mock';
import { usePageMeta } from '../components/Layout';
import { Reveal, SectionHeader, PageHero, CloseCTA, ReviewCard, ProofStrip } from '../components/Shared';

function MapBlock() {
  return (
    <div className="rounded-2xl overflow-hidden border border-[rgba(148,170,220,0.16)] h-[340px]">
      <iframe
        title="PITFIT studio location map — Travis Walk, Dallas"
        src={STUDIO.mapEmbed}
        width="100%"
        height="100%"
        style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) contrast(0.9)' }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
      />
    </div>
  );
}

/* ============ /hiit-classes-dallas (money page) ============ */
export function HiitDallas() {
  usePageMeta(
    'HIIT Classes in Dallas, TX',
    'Looking for HIIT classes in Dallas? PITFIT runs coached, non-stop 45- and 60-minute HIIT classes at Travis Walk in Uptown — rated 4.9 from 77+ reviews. $15 first class for new clients.'
  );

  return (
    <>
      <PageHero
        tag="Dallas · Uptown · Knox-Henderson"
        title="HIIT classes in Dallas, done right."
        sub="Coached, non-stop 45- and 60-minute classes — inside a Travis Walk parking garage. Rated 4.9★ across 77+ reviews and featured in D Magazine."
        video={VIDEOS.hero}
      />
      <ProofStrip />

      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <h2 className="h-section text-white">Why Dallas picks the PIT.</h2>
            <p className="text-muted-pf mt-5 text-[17px] leading-relaxed">
              Dallas has no shortage of HIIT options — big-box circuits, franchise studios, bootcamps in every neighborhood. PITFIT wins on the things that actually change your results: a mix of 45- and 60-minute class formats, class sizes that range from 16 to 40 with a dedicated coach in every room, a founder with 13+ years of coaching pedigree, and a weekly split that lets you train daily without burning out.
            </p>
            <p className="text-muted-pf mt-4 text-[17px] leading-relaxed">
              We're in the Travis Walk garage on Travis Street — minutes from Uptown, Knox-Henderson, Highland Park and the Park Cities, with free parking in the same structure you train in.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/book" className="btn-primary">Book a Class <ArrowRight size={16} /></Link>
              <Link to="/hiit-workout/schedule" className="btn-ghost">View Schedule</Link>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <MapBlock />
            <div className="flex items-start gap-3 mt-5 text-muted-pf text-[15px]">
              <Car size={18} className="shrink-0 mt-0.5" style={{ color: 'var(--pf-brand)' }} />
              <p>Free parking in the Travis Walk garage — enter from Travis Street and follow the PITFIT signs. {STUDIO.address}.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* NEIGHBORHOODS */}
      <section className="section-pad" style={{ background: 'var(--pf-bg2)' }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader tag="Serving Every Neighborhood" title="Close to wherever you are." />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {NEIGHBORHOODS.map((n, i) => (
              <Reveal key={n.slug} delay={i * 80}>
                <Link to={`/hiit-classes-dallas/${n.slug}`} className="glass-card glass-card-hover p-7 h-full flex flex-col group">
                  <MapPin size={20} style={{ color: 'var(--pf-brand2)' }} />
                  <h3 className="font-display font-semibold text-white text-xl mt-4">{n.name}</h3>
                  <p className="font-mono-pf text-[11.5px] uppercase tracking-[0.12em] text-muted-pf mt-1.5">{n.distance}</p>
                  <p className="text-muted-pf text-[14.5px] mt-3 leading-relaxed flex-1">{n.blurb.slice(0, 120)}…</p>
                  <span className="btn-quiet mt-4">HIIT near {n.name} <ArrowRight size={14} /></span>
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="mt-6 grid md:grid-cols-2 gap-5">
            <Reveal>
              <Link to="/locations/uptown-dallas" className="glass-card glass-card-hover p-7 flex flex-col justify-between h-full">
                <div>
                  <h3 className="font-display font-semibold text-white text-xl">Uptown Dallas — our home base</h3>
                  <p className="text-muted-pf text-[14.5px] mt-1.5">The full local hub: address, hours, parking, and everything within walking distance.</p>
                </div>
                <span className="btn-quiet mt-4">Uptown studio hub <ArrowRight size={14} /></span>
              </Link>
            </Reveal>
            <Reveal delay={80}>
              <Link to="/hyrox-classes-dallas" className="glass-card glass-card-hover p-7 flex flex-col justify-between h-full" style={{ borderColor: 'rgba(125,167,255,0.35)' }}>
                <div>
                  <h3 className="font-display font-semibold text-white text-xl">HYROX classes in Dallas</h3>
                  <p className="text-muted-pf text-[14.5px] mt-1.5">Race-specific training 6 days a week — sleds, sandbag lunges, wall balls and compromised running. $15 first class.</p>
                </div>
                <span className="btn-quiet mt-4">HYROX training hub <ArrowRight size={14} /></span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader tag="What Members Say" title="Straight from the reviews." />
          <div className="grid md:grid-cols-2 gap-5">
            {REVIEWS.slice(0, 2).map((r, i) => (
              <Reveal key={r.name} delay={i * 80}><ReviewCard review={r} /></Reveal>
            ))}
          </div>
          <p className="text-muted-pf text-[15px] mt-8">
            Want this week's classes and coaches? The <Link to="/hiit-workout/schedule" className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>live schedule</Link> is synced in real time with the studio's booking system.
          </p>
        </div>
      </section>

      <CloseCTA title="The best HIIT class in Dallas is $15 to try." />
    </>
  );
}

/* ============ Neighborhood pages ============ */
export function Neighborhood() {
  const { slug } = useParams();
  const hood = NEIGHBORHOODS.find((n) => n.slug === slug);

  usePageMeta(
    hood ? hood.h1 : 'HIIT Classes Dallas',
    hood ? `${hood.h1}: PITFIT runs 60-minute non-stop coached HIIT classes at Travis Walk, ${hood.distance.toLowerCase()} from ${hood.name}. $15 first class.` : ''
  );

  if (!hood) return <Navigate to="/hiit-classes-dallas" replace />;

  return (
    <>
      <PageHero tag={`${hood.name} · ${hood.distance}`} title={hood.h1} sub={hood.blurb} img={IMAGES.sweat} />
      <ProofStrip />

      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <h2 className="h-section text-white">Why {hood.name} trains with PITFIT.</h2>
            <ul className="mt-7 space-y-4">
              {hood.points.map((p) => (
                <li key={p} className="flex gap-3.5 text-[16px] text-muted-pf">
                  <Star size={17} className="shrink-0 mt-1" fill="currentColor" style={{ color: 'var(--pf-brand2)' }} />
                  {p}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3 mt-9">
              <Link to="/book" className="btn-primary">Book a Class <ArrowRight size={16} /></Link>
              <Link to="/hiit-workout/schedule" className="btn-ghost">View Schedule</Link>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <MapBlock />
            <p className="text-muted-pf text-[14.5px] mt-4">{STUDIO.address} · free garage parking</p>
          </Reveal>
        </div>
      </section>

      <section className="section-pad !pt-0">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid md:grid-cols-2 gap-5">
            {REVIEWS.slice(2, 4).map((r, i) => (
              <Reveal key={r.name} delay={i * 80}><ReviewCard review={r} /></Reveal>
            ))}
          </div>
          <div className="flex flex-wrap gap-2.5 mt-10">
            <span className="font-mono-pf text-[12px] uppercase tracking-[0.12em] text-muted-pf py-2">Also serving:</span>
            {NEIGHBORHOODS.filter((n) => n.slug !== hood.slug).map((n) => (
              <Link key={n.slug} to={`/hiit-classes-dallas/${n.slug}`} className="font-mono-pf text-[12px] uppercase tracking-[0.12em] px-4 py-2 rounded-full border border-[rgba(148,170,220,0.25)] text-muted-pf hover:text-white hover:border-[rgba(125,167,255,0.5)] transition-colors">
                {n.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CloseCTA title={`${hood.name}: get in the PIT for $15.`} />
    </>
  );
}

/* ============ /locations/uptown-dallas ============ */
export function UptownHub() {
  usePageMeta(
    'Uptown Dallas HIIT Studio — Travis Walk',
    'PITFIT\u2019s Uptown Dallas studio at Travis Walk: address, hours, free parking details and the neighborhoods we serve. 60-minute HIIT classes, $15 first class.'
  );

  return (
    <>
      <PageHero
        tag="The Local Hub"
        title="Uptown Dallas. Travis Walk. The PIT."
        sub="Everything you need to find us, park, and get in the PIT — plus the neighborhoods our members commute from."
        img={IMAGES.industrial}
      />

      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12">
          <Reveal>
            <MapBlock />
          </Reveal>
          <Reveal delay={100}>
            <div className="glass-card p-8 h-full">
              <p className="mono-label mb-4">Studio Details</p>
              <h2 className="font-display font-bold text-white text-2xl">{STUDIO.address}</h2>
              <p className="text-muted-pf mt-2 text-[15px]">{STUDIO.neighborhood}</p>
              <div className="mt-6 space-y-2">
                {STUDIO.hours.map((h) => (
                  <p key={h.d} className="text-[15.5px]"><span className="text-white font-medium">{h.d}:</span> <span className="text-muted-pf">{h.h}</span></p>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-[rgba(148,170,220,0.13)]">
                <p className="text-white font-medium flex items-center gap-2"><Car size={17} style={{ color: 'var(--pf-brand)' }} /> Parking</p>
                <p className="text-muted-pf text-[15px] mt-2">Free parking in the Travis Walk garage — the studio lives inside it on the lower 3rd level. Enter from Travis Street, proceed down to the lower 3rd level, park in any open spot, and follow the signs to the PIT.</p>
              </div>
              <Link to="/book" className="btn-primary w-full mt-7 justify-center">Book a Class <ArrowRight size={16} /></Link>
            </div>
          </Reveal>
        </div>

        <div className="max-w-7xl mx-auto px-5 lg:px-8 mt-12">
          <SectionHeader tag="We Serve" title="Neighborhood guides" />
          <div className="grid sm:grid-cols-3 gap-5">
            {NEIGHBORHOODS.map((n, i) => (
              <Reveal key={n.slug} delay={i * 70}>
                <Link to={`/hiit-classes-dallas/${n.slug}`} className="glass-card glass-card-hover p-6 block h-full">
                  <h3 className="font-display font-semibold text-white text-lg">{n.name}</h3>
                  <p className="font-mono-pf text-[11.5px] uppercase tracking-[0.12em] text-muted-pf mt-1">{n.distance}</p>
                  <span className="btn-quiet mt-3">View guide <ArrowRight size={14} /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CloseCTA />
    </>
  );
}
